import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { effectiveUserSelector } from "@user/store/selectors";
import { VideoEmbedTokenResponse, VideoEmbedTokenSource } from "@videos/models/embedToken";
import { callVideoEmbedTokenAPI } from "@videos/store/thunks/callVideoEmbedTokenAPI";

type UseVideoEmbedTokenResult = {
  embedToken: VideoEmbedTokenResponse | null;
  isLoading: boolean;
  error: string | null;
};

export const useVideoEmbedToken = (
  videoId: string | null,
  enabled: boolean,
  source?: VideoEmbedTokenSource
): UseVideoEmbedTokenResult => {
  const dispatch = useAppDispatch();
  const effectiveUserId = useAppSelector((state) => effectiveUserSelector(state)?.data?.id);
  const [embedToken, setEmbedToken] = useState<VideoEmbedTokenResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !videoId) {
      setEmbedToken(null);
      setIsLoading(false);
      setError(null);

      return undefined;
    }

    let cancelled = false;

    setIsLoading(true);
    setError(null);
    setEmbedToken(null);

    dispatch(callVideoEmbedTokenAPI(videoId, source))
      .then((data) => {
        if (!cancelled) {
          setEmbedToken(data);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("Unable to load video. Please try again later.");
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, effectiveUserId, enabled, source, videoId]);

  return { embedToken, isLoading, error };
};
